import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip, Zoom } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from "../../api/axios";
import { RARITY_COLORS, RarityGrade } from '../utils/RarityColors';
import { formatSteamImageUrl } from '../../hooks/useSteamImageUrl';

interface LandingPageLiveTradesDTO {
  itemImage: string;
  itemName: string;
  rarity: string;
}

const LiveTrade = () => {
  const { t } = useTranslation();
  const [trades, setTrades] = useState<LandingPageLiveTradesDTO[]>([]);

  const fetchTrades = async () => {
    try {
      const res = await api.get<LandingPageLiveTradesDTO[]>("/landing-page/live-trades");
      setTrades(res.data);
    } catch (error) {
      // Fail silently
    }
  };

  useEffect(() => {
    fetchTrades();
    const interval = setInterval(fetchTrades, 10000);
    return () => clearInterval(interval);
  }, []);

  const displayedTrades = trades.slice(-8);

  return (
      <section className="w-full max-w-[1600px] mx-auto px-6 md:px-8 py-8">
        <div className="w-full bg-white dark:bg-neutral-900/30 rounded-3xl p-6 backdrop-blur-md flex flex-col md:flex-row items-center gap-8 overflow-hidden border border-neutral-200 dark:border-white/5 shadow-xl transition-colors duration-300">

          {/* Label Section */}
          <div className="flex flex-col min-w-[200px]">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_12px_rgba(34,197,94,0.6)]" />
              <span className="text-light-text dark:text-dark-text font-black uppercase tracking-tighter text-2xl">{t('landing_page.live_trades.title')}</span>
            </div>
            <span className="text-gray-500 dark:text-slate-500 text-[11px] font-bold uppercase tracking-[0.2em] ml-1">{t('landing_page.live_trades.subtitle')}</span>
          </div>

          <div className="h-16 w-[1px] bg-neutral-200 dark:bg-slate-800 hidden md:block" />

          {/* Activity Section */}
          <div className="flex-1 flex items-center gap-6 overflow-hidden">
            <span className="text-dark-primary font-bold text-sm whitespace-nowrap italic uppercase tracking-widest opacity-80">{t('landing_page.live_trades.recent')}</span>

            <div className="flex items-center gap-3 relative h-24">
              <AnimatePresence initial={false}>
                {displayedTrades.map((trade, index) => {
                  const rarityColor = RARITY_COLORS[trade.rarity as RarityGrade] ?? '#ffffff';

                  return (
                      <motion.div
                          key={`${trade.itemName}-${index}`}
                          layout
                          initial={{ opacity: 0, scale: 0.5, x: -30 }}
                          animate={{ opacity: 1, scale: 1, x: 0 }}
                          exit={{ opacity: 0, scale: 0, x: 30 }}
                          transition={{ type: "spring", stiffness: 400, damping: 28 }}
                      >
                        <Tooltip
                            title={trade.itemName}
                            placement="top"
                            arrow
                            TransitionComponent={Zoom}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: 'var(--tooltip-bg, #171717)',
                                  color: 'var(--tooltip-text, #ffffff)',
                                  '.light &': {
                                    '--tooltip-bg': '#ffffff',
                                    '--tooltip-text': '#171717',
                                  },
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  padding: '8px 12px',
                                  borderRadius: '8px',
                                  border: `1px solid ${rarityColor}bb`, // Border color with transparency
                                  boxShadow: `0 4px 20px ${rarityColor}22`,
                                  position: 'relative',
                                  // Targeting the arrow pseudo-element for the border
                                  '& .MuiTooltip-arrow': {
                                    color: 'var(--tooltip-bg, #171717)',
                                    '&::before': {
                                      border: `1px solid ${rarityColor}bb`, // Add border to arrow
                                      backgroundColor: 'var(--tooltip-bg, #171717)',
                                      boxSizing: 'border-box',
                                    },
                                  },
                                },
                              },
                            }}
                        >
                          <div className="group relative w-20 h-20 flex items-center justify-center">

                            {/* Bottom Rarity Indicator */}
                            <div
                                style={{ backgroundColor: rarityColor }}
                                className="absolute bottom-0 left-0 right-0 h-[4px] z-20 shadow-[0_-4px_10px_rgba(0,0,0,0.3)]"
                            />

                            {/* Rarity Gradient Fade Effect */}
                            <div
                                style={{
                                  background: `linear-gradient(to top, ${rarityColor}44 0%, ${rarityColor}11 40%, transparent 100%)`,
                                }}
                                className="absolute inset-0 z-0 opacity-60 group-hover:opacity-100 transition-opacity duration-300"
                            />

                            <img
                                src={formatSteamImageUrl(trade.itemImage, '100fx100f')}
                                alt={trade.itemName}
                                className="w-16 h-16 object-contain transform group-hover:scale-125 transition-transform duration-500 ease-out z-10 relative drop-shadow-[0_8px_16px_rgba(0,0,0,0.4)]"
                            />
                          </div>
                        </Tooltip>
                      </motion.div>
                  );
                })}
              </AnimatePresence>

              {trades.length > 8 && (
                  <div className="ml-4 bg-neutral-100 dark:bg-neutral-800/80 px-3 py-1.5 rounded-xl border border-neutral-200 dark:border-slate-700 shadow-lg shrink-0">
                    <span className="text-light-text dark:text-white text-[12px] font-black tracking-tighter">+{trades.length - 8}</span>
                  </div>
              )}
            </div>
          </div>
        </div>
      </section>
  );
};

export default LiveTrade;